import { Component, EventEmitter, Input, Output, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import { MaterialModule } from '../../shared/material.module';
import { CartBadgeComponent } from '../../shared/components/cart-badge/cart-badge.component';
import { NavigationService } from '../../shared/services/navigation.service';
import { AuthService } from '../../auth/services/auth.service';
import { ThemeService, Theme, ThemeName } from '../../shared/services/theme.service';
import { CloudService } from '../../shared/services/cloud.service';
import { ModuleStateService } from '../../shared/services/module-state.service';
import { MenuItemType } from '../../shared/models/menu-item.type';

import { Subject } from 'rxjs';
import { takeUntil, filter, take, switchMap } from 'rxjs/operators';
import { MenuService } from '../../shared/services/menu.service';

interface UserProfile {
  userId: number;
  userName: string;
  email?: string;
  profileImagePath?: string;
  fullName?: string;
  userCode?: string; // Member ID like WBSU250700
}

@Component({
  selector: 'app-header',
  standalone: true,
  imports: [CommonModule, RouterModule, MaterialModule, CartBadgeComponent],
  templateUrl: './header.component.html',
  styleUrl: './header.component.scss'
})
export class HeaderComponent implements OnInit, OnDestroy {
  @Input() isSidebarOpen = false;
  @Output() toggleSidebar = new EventEmitter<void>();
  private destroy$ = new Subject<void>();

  currentPageTitle = 'Dashboard';

  // New nav state
  selectedModule: MenuItemType | null = null;
  isPagesEnabled = false;
  breadcrumbs: string[] = [];
  currentUser: UserProfile | null = null;
  userInitials = '';
  profileImageUrl = '';
  defaultProfileImage = 'assets/images/profiles/default.svg'; // Default profile image
  showInitials = false; // Flag to show initials when image fails
  
  // Theme properties
  availableThemes: Theme[] = [];
  currentTheme: ThemeName = 'pink';
  currentThemeIcon = 'palette';

  constructor(
    private router: Router,
    private navigationService: NavigationService,
    private authService: AuthService,
    private themeService: ThemeService,
    private cloudService: CloudService,
    public moduleStateService: ModuleStateService,
    private menuService: MenuService
  ) {}

  ngOnInit(): void {
    // Subscribe to page title changes
    this.navigationService.pageTitle$
      .pipe(takeUntil(this.destroy$))
      .subscribe(title => {
        this.currentPageTitle = title;
      });
      
    // Subscribe to breadcrumbs
    this.navigationService.breadcrumbs$
      .pipe(takeUntil(this.destroy$))
      .subscribe(breadcrumbs => {
        this.breadcrumbs = breadcrumbs;
      });

    // Subscribe to user changes so header updates immediately
    this.authService.currentUser$
      .pipe(takeUntil(this.destroy$))
      .subscribe(user => {
        this.currentUser = user as unknown as UserProfile;
        this.showInitials = false;
        this.generateUserInitials();
        this.loadProfileImage();
      });
    
    // Initialize theme
    this.initializeTheme();

    // Track selected module
    this.moduleStateService.selectedModule$
      .pipe(takeUntil(this.destroy$))
      .subscribe(mod => {
        this.selectedModule = mod;
        this.isPagesEnabled = !!mod;
      });

    // Ensure modules are loaded even when modules-landing page was not visited
    this.moduleStateService.allModules$
      .pipe(
        take(1),
        filter(list => list.length === 0),
        switchMap(() => this.menuService.getFeatureHierarchyForMenu('WEB')),
        takeUntil(this.destroy$)
      )
      .subscribe(items => {
        const modules = items.filter(i => i.isView || i.hasChildren);
        this.moduleStateService.setModules(modules);
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadCurrentUser(): void {
    // Backward compatibility: initial bootstrap if needed.
    try {
      const userJson = localStorage.getItem('currentUser');
      if (userJson && !this.currentUser) {
        this.currentUser = JSON.parse(userJson);
        this.generateUserInitials();
        this.loadProfileImage();
      }
    } catch {
      // ignore
    }
  }

  private generateUserInitials(): void {
    if (!this.currentUser) return;
    
    const name = this.currentUser.fullName || this.currentUser.userName || '';
    const nameParts = name.trim().split(' ');
    
    if (nameParts.length >= 2) {
      this.userInitials = (nameParts[0][0] + nameParts[nameParts.length - 1][0]).toUpperCase();
    } else if (nameParts.length === 1 && nameParts[0]) {
      this.userInitials = nameParts[0].substring(0, 2).toUpperCase();
    } else {
      this.userInitials = 'U';
    }
  }

  private loadProfileImage(): void {
    if (this.currentUser?.profileImagePath) {
      // Construct the full URL for the profile image
      this.profileImageUrl = this.getProfileImageUrl(this.currentUser.profileImagePath);
    } else {
      // Use default image if no profile image path
      this.profileImageUrl = '';
    }
  }

  private getProfileImageUrl(path: string): string {
    // If it's already a full URL, return as is
    if (path.startsWith('http://') || path.startsWith('https://')) {
      return path;
    }

    // Use API public image endpoint (works even if S3 bucket is private)
    // Add cache-buster to avoid stale avatar after update.
    return `${this.cloudService.getPublicFileUrl(path)}?v=${Date.now()}`;
  }

  // Handle image load error - fallback to default image or initials
  onImageError(event: any): void {
    // Try to load default image first
    if (event.target.src !== this.defaultProfileImage) {
      event.target.src = this.defaultProfileImage;
    } else {
      // If default image also fails, hide image and show initials
      this.showInitials = true;
      this.profileImageUrl = '';
    }
  }

  getUserDisplayName(): string {
    if (!this.currentUser) return 'Guest User';
    return this.currentUser.fullName || this.currentUser.userName || 'User';
  }

  getUserId(): string {
    if (!this.currentUser) return '';
    // Display Member ID (userCode) if available, otherwise fall back to userId
    const memberId = this.currentUser.userCode || `ID: ${this.currentUser.userId}`;
    return this.currentUser.userCode ? `Member ID: ${memberId}` : memberId;
  }

  onToggleSidebar(): void {
    this.toggleSidebar.emit();
  }

  onModulesClick(): void {
    this.moduleStateService.openModulesSidebar();
  }

  onPagesClick(): void {
    this.moduleStateService.openPagesSidebar();
  }

  navigateToChangePassword(): void {
    this.router.navigate(['/change-password']);
  }

  navigateToResetPassword(): void {
    this.router.navigate(['/request-reset-password']);
  }

  logout(): void {
    // Use AuthService logout method to properly clear authentication state
    this.authService.logout();
  }
  
  // Theme Management Methods
  private initializeTheme(): void {
    // Get available themes
    this.availableThemes = this.themeService.availableThemes;
    
    // Subscribe to theme changes
    this.themeService.currentTheme$
      .pipe(takeUntil(this.destroy$))
      .subscribe(theme => {
        this.currentTheme = theme;
        this.updateThemeIcon();
      });
  }
  
  private updateThemeIcon(): void {
    const themeDetails = this.themeService.getThemeDetails(this.currentTheme);
    if (themeDetails) {
      this.currentThemeIcon = themeDetails.icon;
    }
  }
  
  changeTheme(themeName: ThemeName): void {
    this.themeService.setTheme(themeName);
  }
  
  getCurrentThemeDisplayName(): string {
    return this.themeService.getCurrentThemeDisplayName();
  }
}
